// src/utils/cache.ts
//
// Two-layer, TTL-based cache.
//   1. In-memory Map  — fast, per-tab, lost on reload.
//   2. localStorage   — opt-in, survives reloads and tab restarts.
//
// Persistence is opt-in via `{ persist: true }`. Payloads containing Firestore
// `Timestamp` objects are walked on write (Timestamp → marker object) and
// restored on read so they survive the JSON round-trip with `.toDate()` intact.
//
// Usage:
//   import { withCache } from "../utils/cache";
//   const data = await withCache("athletes:all", 5 * 60 * 1000, getAllAthletes);
//   const lite = await withCache("athletes:aggregate", 5 * 60 * 1000, fetcher, { persist: true });

import { Timestamp } from "firebase/firestore";

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

export interface WithCacheOptions {
  /** When true, also mirror writes to localStorage and read from it on cold start. */
  persist?: boolean;
}

// ── Timestamp-safe serialization ─────────────────────────────────────────────
// Firestore Timestamps are objects with a `toDate()` method that don't survive
// JSON.stringify (they serialize as {seconds, nanoseconds} but lose their
// prototype, so consumers calling `.toDate()` crash).
//
// We walk the payload depth-first, replacing each Timestamp with a tagged
// marker object on write, and reviving it back to a real Timestamp on read.
// The walk skips functions/symbols/Dates (Dates also lose their prototype but
// no caller currently stores raw Dates in the cache).
const TS_MARKER = "__ts__";

function isTimestamp(v: unknown): v is Timestamp {
  if (!v || typeof v !== "object") return false;
  const t = v as { toDate?: unknown; seconds?: unknown; nanoseconds?: unknown };
  return typeof t.toDate === "function" &&
    typeof t.seconds === "number" &&
    typeof t.nanoseconds === "number";
}

function encodeTimestamps(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (isTimestamp(value)) {
    return {
      [TS_MARKER]: true,
      seconds: (value as Timestamp).seconds,
      nanoseconds: (value as Timestamp).nanoseconds,
    };
  }
  if (Array.isArray(value)) return value.map(encodeTimestamps);
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>)) {
      out[k] = encodeTimestamps((value as Record<string, unknown>)[k]);
    }
    return out;
  }
  return value;
}

function decodeTimestamps(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(decodeTimestamps);
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (
      obj[TS_MARKER] === true &&
      typeof obj.seconds === "number" &&
      typeof obj.nanoseconds === "number"
    ) {
      return new Timestamp(obj.seconds as number, obj.nanoseconds as number);
    }
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) {
      out[k] = decodeTimestamps(obj[k]);
    }
    return out;
  }
  return value;
}

// ── In-memory store ──────────────────────────────────────────────────────────
// Lives at module scope — shared across all components within the same tab,
// never persisted beyond a page reload (the localStorage layer handles that).
const store = new Map<string, CacheEntry<unknown>>();

// Tracks in-flight fetcher promises so two simultaneous callers sharing the
// same key never trigger two Firestore reads.
const inflight = new Map<string, Promise<unknown>>();

// ── localStorage layer ───────────────────────────────────────────────────────
// All persisted entries live under STORAGE_PREFIX so we can sweep them out
// when CURRENT_CACHE_VERSION changes (e.g. after a deploy that changes a
// cached payload shape).
const STORAGE_PREFIX = "cache:v2:";
const CACHE_VERSION_KEY = "cache_version";
// Bumped to 2 when the Timestamp serializer landed — old entries stored
// Timestamps as raw {seconds, nanoseconds} objects without the marker, so
// they need to be flushed before the decoder can run safely.
const CURRENT_CACHE_VERSION = "3";

// One-time bootstrap on module load: if the stored version doesn't match the
// current code's version, wipe every cache:* entry so we don't deserialize
// records into a stale shape.
(function bootstrapCacheVersion() {
  try {
    if (typeof localStorage === "undefined") return;
    const stored = localStorage.getItem(CACHE_VERSION_KEY);
    if (stored !== CURRENT_CACHE_VERSION) {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) localStorage.removeItem(k);
      }
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
    }
  } catch {
    // localStorage unavailable (SSR, privacy mode, quota) — silently degrade.
  }
})();

function readFromStorage<T>(key: string): CacheEntry<T> | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    if (typeof entry?.expiresAt !== "number") return null;
    if (entry.expiresAt <= Date.now()) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }
    entry.value = decodeTimestamps(entry.value) as T;
    return entry;
  } catch {
    return null;
  }
}

function writeToStorage<T>(key: string, entry: CacheEntry<T>): void {
  try {
    if (typeof localStorage === "undefined") return;
    const encoded: CacheEntry<unknown> = {
      value: encodeTimestamps(entry.value),
      expiresAt: entry.expiresAt,
    };
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(encoded));
  } catch {
    // Quota exceeded or storage disabled — keep the in-memory copy and move on.
  }
}

function removeFromStorage(key: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    /* ignore */
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns a cached value if one exists and hasn't expired, otherwise calls
 * `fetcher`, caches its result for `ttlMs` ms, and returns it.
 *
 * @param key     Unique cache key (e.g. "athletes:all", "clubs:all").
 * @param ttlMs   Time-to-live in milliseconds.
 * @param fetcher Async function that performs the real Firestore read.
 * @param options `{ persist: true }` to mirror to localStorage (JSON-safe payloads only).
 */
export async function withCache<T>(
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>,
  options?: WithCacheOptions
): Promise<T> {
  const persist = options?.persist === true;
  const now = Date.now();

  // 1. In-memory hit.
  const entry = store.get(key);
  if (entry && entry.expiresAt > now) {
    return entry.value as T;
  }

  // 2. localStorage hit (only for opt-in keys). Hydrate memory on success.
  if (persist) {
    const persisted = readFromStorage<T>(key);
    if (persisted) {
      store.set(key, persisted as CacheEntry<unknown>);
      return persisted.value;
    }
  }

  // 3. Dedupe concurrent callers awaiting the same key.
  if (inflight.has(key)) {
    return inflight.get(key) as Promise<T>;
  }

  const promise = (async () => {
    try {
      const value = await fetcher();
      const newEntry: CacheEntry<T> = { value, expiresAt: Date.now() + ttlMs };
      store.set(key, newEntry as CacheEntry<unknown>);
      if (persist) writeToStorage(key, newEntry);
      return value;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise as Promise<T>;
}

/**
 * Removes one key from both memory and localStorage.
 */
export function invalidateCache(key: string): void {
  store.delete(key);
  removeFromStorage(key);
}

/**
 * Removes all keys (in memory and localStorage) that start with `prefix`.
 */
export function invalidateCachePrefix(prefix: string): void {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) {
      store.delete(key);
    }
  }
  try {
    if (typeof localStorage === "undefined") return;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (
        k &&
        k.startsWith(STORAGE_PREFIX) &&
        k.slice(STORAGE_PREFIX.length).startsWith(prefix)
      ) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}
